// IncomeStatement.jsx
import { useState, useEffect } from "react";
import Sidebar from "../components/Sidebar";
import Navbar from "../components/Navbar";
import ExcelJS from "exceljs";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import api from '../config/api';

export default function IncomeStatement() {
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [isLoading, setIsLoading] = useState(false);
  const [accountCodes, setAccountCodes] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [reportData, setReportData] = useState(null);

  function isLeapYear(year) {
    return (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0);
  }

  const getMonths = (year) => [
    { name: "January", start: "01", end: "31" },
    { name: "February", start: "02", end: isLeapYear(year) ? "29" : "28" },
    { name: "March", start: "03", end: "31" },
    { name: "April", start: "04", end: "30" },
    { name: "May", start: "05", end: "31" },
    { name: "June", start: "06", end: "30" },
    { name: "July", start: "07", end: "31" },
    { name: "August", start: "08", end: "31" },
    { name: "September", start: "09", end: "30" },
    { name: "October", start: "10", end: "31" },
    { name: "November", start: "11", end: "30" },
    { name: "December", start: "12", end: "31" },
  ];

  const fetchReport = async (month) => {
    setIsLoading(true);
    try {
      const startDate = `${selectedYear}-${month.start}-01`;
      const endDate = `${selectedYear}-${month.start}-${month.end}`;
      const token = localStorage.getItem("token");

      const [txRes, codeRes] = await Promise.all([
        fetch(api(`/transactions/by-date?start=${startDate}&end=${endDate}`), {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(api("/account_codes/"), {
          headers: { Authorization: `Bearer ${token}` },
        })
      ]);

      const txData = await txRes.json();
      const codeData = await codeRes.json();
      setTransactions(txData);
      setAccountCodes(codeData);

      const pendapatan = {};
      const beban = {};

      txData.forEach(tx => {
        const acc = codeData.find(a => a.code === tx.account_code);
        if (!acc) return;
        const key = acc.name;

        if (tx.transaction_type === "credit") {
          pendapatan[key] = (pendapatan[key] || 0) + tx.amount;
        } else if (tx.transaction_type === "debit") {
          beban[key] = (beban[key] || 0) + tx.amount;
        }
      });

      const totalPendapatan = Object.values(pendapatan).reduce((a, b) => a + b, 0);
      const totalBeban = Object.values(beban).reduce((a, b) => a + b, 0);

      setReportData({
        month: month.name,
        pendapatan,
        totalPendapatan,
        beban,
        totalBeban,
        labaRugi: totalPendapatan - totalBeban
      });
    } catch (err) {
      console.error("Gagal mengambil laporan:", err);
      alert("Gagal mengambil data laporan.");
    } finally {
      setIsLoading(false);
    }
  };

  const downloadExcel = async () => {
    if (!reportData) return;
    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet("Income Statement");

    const header = sheet.addRow([`Income Statement TOV ${reportData.month.toUpperCase()}-${selectedYear.toString().slice(2)}`]);
    header.font = { bold: true, size: 14 };
    sheet.addRow([]);

    const sectionHeader = (title) => {
      const row = sheet.addRow([title, "Jumlah"]);
      row.eachCell((cell) => {
        cell.font = { bold: true };
        cell.alignment = { vertical: "middle", horizontal: "left" };
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "D9E1F2" },
        };
        cell.border = {
          top: { style: "thin" },
          bottom: { style: "thin" },
          left: { style: "thin" },
          right: { style: "thin" },
        };
      });
    };

    const addDataRows = (dataObj) => {
      Object.entries(dataObj).forEach(([name, amount], index) => {
        const row = sheet.addRow([name, amount]);
        row.eachCell((cell) => {
          cell.border = {
            top: { style: "thin" },
            bottom: { style: "thin" },
            left: { style: "thin" },
            right: { style: "thin" },
          };
          if (index % 2 === 0) {
            cell.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: "F2F2F2" },
            };
          }
        });
      });
    };

    sectionHeader("Pendapatan");
    addDataRows(reportData.pendapatan);
    const totalPendRow = sheet.addRow(["Total Pendapatan", reportData.totalPendapatan]);
    totalPendRow.font = { bold: true };

    sheet.addRow([]);
    sectionHeader("Beban Operasional");
    addDataRows(reportData.beban);
    sheet.addRow(["Total Beban", reportData.totalBeban]).font = { bold: true };
    sheet.addRow(["Laba/Rugi", reportData.labaRugi]).font = { bold: true };

    sheet.columns[1].numFmt = '"Rp" #,##0';

    sheet.columns = [
      { width: 40 }, // Nama akun atau label
      { width: 20 }  // Jumlah
    ];

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `income_statement_${reportData.month.toLowerCase()}_${selectedYear}.xlsx`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadPDF = () => {
    if (!reportData) return;
    const doc = new jsPDF();
    doc.setFontSize(14);
    doc.text(`Income Statement TOV ${reportData.month.toUpperCase()}-${selectedYear.toString().slice(2)}`, 14, 15);

    autoTable(doc, {
      startY: 25,
      head: [["Pendapatan", "Jumlah"]],
      body: [
        ...Object.entries(reportData.pendapatan).map(([name, amount]) => [name, `Rp ${amount.toLocaleString()}`]),
        ["Total Pendapatan", `Rp ${reportData.totalPendapatan.toLocaleString()}`],
      ],
    });

    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 10,
      head: [["Beban Operasional", "Jumlah"]],
      body: [
        ...Object.entries(reportData.beban).map(([name, amount]) => [name, `Rp ${amount.toLocaleString()}`]),
        ["Total Beban", `Rp ${reportData.totalBeban.toLocaleString()}`],
        ["Laba/Rugi", `Rp ${reportData.labaRugi.toLocaleString()}`],
      ],
    });

    doc.save(`income_statement_${reportData.month.toLowerCase()}_${selectedYear}.pdf`);
  };

  return (
    <div className="layout-wrapper layout-content-navbar">
      <div className="layout-container">
        <Sidebar />
        <div className="layout-page">
          <Navbar />
          <div className="content-wrapper">
            <div className="container-xxl flex-grow-1 container-p-y">
              <h2 className="mb-4">Income Statement</h2>
              <div className="card mb-4">
                <div className="card-body">
                  <div className="row mb-4">
                    <div className="col-md-4">
                      <label className="form-label">Select Year:</label>
                      <select
                        className="form-select"
                        value={selectedYear}
                        onChange={(e) => setSelectedYear(Number(e.target.value))}
                      >
                        {[currentYear - 1, currentYear, currentYear + 1].map((year) => (
                          <option key={year} value={year}>{year}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="row">
                    {getMonths(selectedYear).map((month) => (
                      <div className="col-md-3 mb-3" key={month.name}>
                        <button
                          className="btn btn-primary w-100"
                          onClick={() => fetchReport(month)}
                          disabled={isLoading}
                        >
                          {isLoading ? (
                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                          ) : month.name}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              {reportData && (
                <div className="card">
                  <div className="card-body">
                    <h4 className="text-center mb-4">Income Statement TOV {reportData.month.toUpperCase()}-{selectedYear.toString().slice(2)}</h4>
                    <table className="table table-bordered">
                      <thead>
                        <tr className="table-warning text-center">
                          <th colSpan={2}>Pendapatan</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(reportData.pendapatan).map(([name, amount]) => (
                          <tr key={name}>
                            <td>{name}</td>
                            <td className="text-end">Rp {amount.toLocaleString()}</td>
                          </tr>
                        ))}
                        <tr className="fw-bold">
                          <td>Total Pendapatan</td>
                          <td className="text-end">Rp {reportData.totalPendapatan.toLocaleString()}</td>
                        </tr>
                      </tbody>
                      <thead>
                        <tr className="table-warning text-center">
                          <th colSpan={2}>Beban Operasional</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(reportData.beban).map(([name, amount]) => (
                          <tr key={name}>
                            <td>{name}</td>
                            <td className="text-end">Rp {amount.toLocaleString()}</td>
                          </tr>
                        ))}
                        <tr className="fw-bold">
                          <td>Total Beban</td>
                          <td className="text-end">Rp {reportData.totalBeban.toLocaleString()}</td>
                        </tr>
                        <tr className="fw-bold table-bordered">
                          <td>Laba/Rugi</td>
                          <td className="text-end">Rp {reportData.labaRugi.toLocaleString()}</td>
                        </tr>
                      </tbody>
                    </table>
                    <div className="d-flex gap-3 mt-4">
                      <button className="btn btn-primary" onClick={downloadExcel}>Download as Excel</button>
                      <button className="btn btn-primary" onClick={downloadPDF}>Download as PDF</button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
